import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { AvailableLayer, Feature, LayerItemDef, SelectedLayers } from './layer.model';
import {
    changeAvailableLayers,
    clearLayers,
    failLayer,
    layerFeaturesLoaded,
    layersDefLoaded,
    mapLayersNamesToggle,
    removeLayers,
    selectLayer,
    toggleLayerUpdate,
    toggleShowParcels,
    unselectLayer
} from './layers.actions';

export interface State {
    layersDef?: LayerItemDef[];
    mapLayerNames: boolean;
    selection: SelectedLayers;
    availableLayers: AvailableLayer[];
    features: { [layerKey: string]: Feature[] };
    showParcels: boolean;
}

export const initialState: State = {
    mapLayerNames: false,
    selection: {},
    availableLayers: [],
    features: {},
    showParcels: false,
};

const layersReducer = createReducer(
    initialState,
    on(layersDefLoaded, (state, { layersDef }) => ({
        ...state,
        layersDef,
    })),
    on(changeAvailableLayers, (state, { availableLayers }) => ({
        ...state,
        availableLayers,
    })),
    on(selectLayer, (state, { key, args, updating }) => ({
        ...state,
        selection: {
            ...state.selection,
            [key]: { args, updating },
        },
    })),
    on(toggleLayerUpdate, (state, { key, updating }) => {
        if (!state.selection[key]) {
            return state;
        }
        return {
            ...state,
            selection: {
                ...state.selection,
                [key]: { args: state.selection[key].args, updating },
            },
        };
    }),
    on(unselectLayer, failLayer, (state, { key }) => {
        let selection = state.selection;
        if (selection[key]) {
            selection = { ...selection };
            delete selection[key];
        }
        let features = state.features;
        if (features[key]) {
            features = { ...features };
            delete features[key];
        }
        return {
            ...state,
            selection,
            features,
        };
    }),
    on(removeLayers, (state, { keys }) => {
        let features = state.features;
        const keysToRemove = Object.keys(features).filter(k => keys.includes(k));
        if (keysToRemove.length > 0) {
            features = { ...features };
            for (const key of keysToRemove) {
                delete features[key];
            }
        }
        return {
            ...state,
            features,
        };
    }),
    on(clearLayers, state => ({
        ...state,
        selection: {},
        features: {},
    })),
    on(layerFeaturesLoaded, (state, { layerKey, features }) => {
        if (!state.selection[layerKey]) {
            return state;
        }
        return {
            ...state,
            features: {
                ...state.features,
                [layerKey]: features,
            },
        };
    }),
    on(mapLayersNamesToggle, (state, { mapLayerNames }) => ({
        ...state,
        mapLayerNames,
    })),
    on(toggleShowParcels, (state, { value }) => ({
        ...state,
        showParcels: value,
    })),
);


export function reducer(state: State | undefined, action: Action) {
    return layersReducer(state, action);
}


const featureSelector = createFeatureSelector<State>('layers');

export const getLayersDef = createSelector(featureSelector, (state: State) => state.layersDef);
export const getAvailableLayers = createSelector(featureSelector, (state: State) => state.availableLayers);
export const getSelectedLayers = createSelector(featureSelector, (state: State) => state.selection);
export const getFeatures = createSelector(featureSelector, (state: State) => state.features);
export const getMapLayersNames = createSelector(featureSelector, (state: State) => state.mapLayerNames);
export const getShowParcels = createSelector(featureSelector, (state: State) => state.showParcels);
